/**
 * Peer review escalation notification API client.
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiGet, apiRequest } from '../utils/apiClient';

export type PeerReviewNotificationEventType =
  | 'Assignment'
  | 'Reminder'
  | 'Timeout'
  | 'ChainExhausted';

export interface PeerReviewNotificationMetadata {
  order_index?: number;
  review_minutes?: number;
  review_deadline_at?: string;
  reminder_attempt?: number;
  peer_review_id?: number;
  study_instance_uid?: string | null;
  report_id?: number | null;
  accession_number?: string;
  patient_name?: string | null;
  patient_uhid?: string | null;
  modality?: string | null;
  test_name?: string | null;
}

export interface PeerReviewNotification {
  id: number;
  facility_id: number;
  workflow_id: number | null;
  peer_review_id: number | null;
  study_id_fk: number | null;
  study_instance_uid: string | null;
  recipient_user_id: number;
  recipient_role: string;
  event_type: PeerReviewNotificationEventType;
  event_key: string;
  message: string;
  metadata: PeerReviewNotificationMetadata | null;
  is_read: boolean;
  created_at: string;
  updated_at: string;
}

interface UnreadCountResponse {
  success: boolean;
  data: { count: number };
}

interface ListResponse {
  success: boolean;
  data: PeerReviewNotification[];
}

const EVENT_LABELS: Record<PeerReviewNotificationEventType, string> = {
  Assignment: 'Peer Review Assigned',
  Reminder: 'Peer Review Reminder',
  Timeout: 'Peer Review Timed Out',
  ChainExhausted: 'Peer Review Chain Exhausted',
};

export function getPeerReviewEventLabel(eventType: string): string {
  if (eventType in EVENT_LABELS) {
    return EVENT_LABELS[eventType as PeerReviewNotificationEventType];
  }
  return 'Peer Review';
}

export async function getPeerReviewUnreadCount(): Promise<number> {
  const response = await apiGet<UnreadCountResponse>(
    API_ENDPOINTS.PEER_REVIEW_NOTIFICATIONS.UNREAD_COUNT,
    { requireAuth: true }
  );
  return response.data?.data?.count ?? 0;
}

export async function listPeerReviewNotifications(
  limit = 50,
  options: { unreadOnly?: boolean } = {}
): Promise<PeerReviewNotification[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (options.unreadOnly) {
    params.set('unread_only', 'true');
  }
  const url = `${API_ENDPOINTS.PEER_REVIEW_NOTIFICATIONS.LIST}?${params.toString()}`;
  const response = await apiGet<ListResponse>(url, { requireAuth: true });
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

export async function markPeerReviewNotificationRead(id: number): Promise<void> {
  await apiRequest(API_ENDPOINTS.PEER_REVIEW_NOTIFICATIONS.MARK_READ(id), {
    method: 'PATCH',
    requireAuth: true,
  });
}

export async function markAllPeerReviewNotificationsRead(): Promise<void> {
  await apiRequest(API_ENDPOINTS.PEER_REVIEW_NOTIFICATIONS.MARK_ALL_READ, {
    method: 'PATCH',
    requireAuth: true,
  });
}

/**
 * Assignment/Reminder → viewer (peer assessment) when study UID + peer_review_id exist;
 * otherwise Peer Review worklist.
 * ChainExhausted (admins) → the exact exhausted workflow in the manual-action queue
 * (`needs_action=exhausted`, which the API answers with the latest Assigned or
 * Expired assignment instead of the default completed Submitted queue).
 */
export function buildPeerReviewNotificationHref(notification: PeerReviewNotification): string {
  const meta = notification.metadata || {};
  const studyUid = notification.study_instance_uid || meta.study_instance_uid || null;
  const peerReviewId = notification.peer_review_id ?? meta.peer_review_id ?? null;

  if (
    (notification.event_type === 'Assignment' || notification.event_type === 'Reminder') &&
    studyUid &&
    peerReviewId != null
  ) {
    const query = new URLSearchParams();
    query.set('StudyInstanceUIDs', studyUid);
    query.set('peerReviewId', String(peerReviewId));
    query.set('returnTo', '/peer-reviews');
    return `/viewer?${query.toString()}`;
  }

  if (notification.event_type === 'ChainExhausted') {
    const query = new URLSearchParams({ needs_action: 'exhausted' });
    if (notification.workflow_id != null) {
      query.set('workflow_id', String(notification.workflow_id));
    }
    if (studyUid) {
      query.set('study_instance_uid', studyUid);
    }
    return `/peer-reviews?${query.toString()}`;
  }

  return '/peer-reviews';
}
